import { zodResolver } from '@hookform/resolvers/zod'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import SendIcon from '@mui/icons-material/Send'
import { Stack, TextField, Button, CircularProgress, InputAdornment, IconButton } from '@mui/material'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@modules/Auth/hooks/useAuth'
import { useGetErrorMessage } from '@modules/Shared/hooks/useGetErrorMessage'
import { globalStore } from '@modules/Shared/store/globalStore'

import { SignInAuth, SignInAuthSchema } from '@context/Auth/domain/SignIn'

export const SignInForm = () => {
  const { openSnackbarAlert } = globalStore()
  const { t } = useTranslation(['common'])
  const { login } = useAuth()
  const [loading, setLoading] = useState(false)
  const { getErrorMessage } = useGetErrorMessage()
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInAuth>({
    resolver: zodResolver(SignInAuthSchema),
  })

  const onSubmit = useCallback(
    async (data: SignInAuth) => {
      setLoading(true)
      const { error } = await login(data)
      if (error && openSnackbarAlert) {
        openSnackbarAlert({
          openSnackBar: true,
          severity: 'error',
          message: 'Solo se permiten usuarios administradores',
        })
      }
      setLoading(false)
    },
    [login, openSnackbarAlert],
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {loading && <CircularProgress />}
      <Stack spacing={2}>
        <TextField
          required
          id="email"
          type="email"
          label={t('email')}
          fullWidth
          {...register('email')}
          error={!!errors.email}
          helperText={getErrorMessage(errors.email) as string}
          autoComplete="email"
        />
        <TextField
          required
          id="password"
          label={t('password')}
          type={showPassword ? 'text' : 'password'}
          fullWidth
          {...register('password')}
          error={!!errors.password}
          helperText={getErrorMessage(errors.password)}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack spacing={2} direction="row" justifyContent="right">
          <Button type="submit" variant="contained" color="primary" endIcon={<SendIcon />}>
            {t('signIn')}
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}
